import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { db, functions } from "../../firebase";

type ParentDoc = {
  parentKey: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  familyId?: string;
  createdAt?: any;
  updatedAt?: any;
  uid?: string;
  linkedEmail?: string;
  linkedAt?: any;
};

type FamilyRow = {
  id: string;
  familyName: string;
  familyCodeUpper?: string;
};

type Row = { id: string } & ParentDoc;

function norm(s: string) {
  return s.trim();
}

function normEmail(s: string) {
  return s.trim().toLowerCase();
}

function fullName(r: { firstName?: string; lastName?: string }) {
  return `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim();
}

export function ParentsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [families, setFamilies] = useState<FamilyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [familyId, setFamilyId] = useState("");
  const [sendInviteNow, setSendInviteNow] = useState(true);
  const [saving, setSaving] = useState(false);

  const [filter, setFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const qRef = query(collection(db, "parents"), orderBy("lastName", "asc"));
      const snap = await getDocs(qRef);
      const out: Row[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      setRows(out);
    } catch (e: any) {
      setErr(e?.message || "Failed to load parents.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const qRef = query(collection(db, "families"), orderBy("familyName", "asc"));
    return onSnapshot(
      qRef,
      (snap) => {
        const out: FamilyRow[] = snap.docs.map((d) => {
          const data: any = d.data();
          return {
            id: d.id,
            familyName: data.familyName ?? d.id,
            familyCodeUpper: data.familyCodeUpper ?? "",
          };
        });
        setFamilies(out);
      },
      (e) => setErr(e?.message || "Failed to load families.")
    );
  }, []);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();

    return rows.filter((r) => {
      if (!showInactive && r.isActive === false) return false;
      if (!f) return true;

      const hay =
        `${r.parentKey} ${r.firstName} ${r.lastName} ${r.email} ${r.uid ?? ""} ${r.familyId ?? ""}`.toLowerCase();

      return hay.includes(f);
    });
  }, [rows, filter, showInactive]);

  async function inviteAndLinkParent(emailValue: string, parentDocId: string) {
    const inviteFn = httpsCallable(functions, "createParentInvite");
    await inviteFn({ email: emailValue, sendEmailNow: true });

    const linkFn = httpsCallable(functions, "linkParentAccount");
    await linkFn({ parentDocId, email: emailValue });
  }

  async function assignFamily(parentDocId: string, emailValue: string, familyIdValue: string) {
    const fn = httpsCallable(functions, "assignParentToFamilyAdmin");
    await fn({
      parentDocId,
      email: emailValue,
      familyId: familyIdValue,
    });
  }

  async function addParent() {
    setErr(null);
    setSaving(true);
    try {
      const emailNorm = normEmail(email);
      const familyIdNorm = norm(familyId);
      const fn = httpsCallable(functions, "createParent");
      const res: any = await fn({
        firstName: norm(firstName),
        lastName: norm(lastName),
        email: emailNorm,
        familyId: familyIdNorm || null,
        sendInviteNow,
      });

      const parentKey = String(res?.data?.parentKey ?? "");
      let inviteWarning = "";
      if (sendInviteNow && emailNorm && parentKey) {
        try {
          await inviteAndLinkParent(emailNorm, parentKey);
        } catch (inviteError: any) {
          inviteWarning = inviteError?.message || "Parent was added, but the invite email could not be sent.";
        }
      }

      setFirstName("");
      setLastName("");
      setEmail("");
      setFamilyId("");

      await load();
      alert(
        inviteWarning
          ? `Parent added successfully. Assigned key: ${parentKey}\n\nInvite/link warning: ${inviteWarning}`
          : `Parent added successfully. Assigned key: ${parentKey}`
      );
    } catch (e: any) {
      setErr(e?.message || "Failed to add parent.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: Row) {
    setErr(null);
    try {
      await updateDoc(doc(db, "parents", row.id), {
        isActive: !row.isActive,
        updatedAt: serverTimestamp(),
      });
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to update parent.");
    }
  }

  async function resendInvite(row: Row) {
    setErr(null);
    setBusyRowId(row.id);
    try {
      if (!row.email) throw new Error("Parent record is missing email.");
      await inviteAndLinkParent(normEmail(row.email), row.id);
      await load();
      alert(`Invite resent to ${row.email}.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to resend invite.");
    } finally {
      setBusyRowId(null);
    }
  }

  async function manualLink(row: Row) {
    setErr(null);
    setBusyRowId(row.id);
    try {
      if (!row.email) throw new Error("Parent record is missing email.");
      const fn = httpsCallable(functions, "linkParentAccount");
      await fn({ parentDocId: row.id, email: normEmail(row.email) });
      await load();
      alert(`Linked ${row.email} to parent ${row.parentKey}.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to link parent account.");
    } finally {
      setBusyRowId(null);
    }
  }

  async function setFamilyForRow(row: Row, nextFamilyId: string) {
    setErr(null);
    setBusyRowId(row.id);
    try {
      const familyValue = String(nextFamilyId ?? "").trim();
      if (!familyValue) return;

      await assignFamily(row.id, normEmail(row.email), familyValue);
      await load();
      alert(`Assigned ${row.email} to family ${familyValue}.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to assign family.");
    } finally {
      setBusyRowId(null);
    }
  }

  async function unlinkFamilyForRow(row: Row) {
    if (!row.familyId) return;
    if (!confirm(`Unlink ${fullName(row) || row.email} from family ${familyLabel(row.familyId)}?\n\nThis will not delete the parent. It only detaches the parent from the household and removes family-derived student links.`)) return;

    setErr(null);
    setBusyRowId(row.id);
    try {
      const fn = httpsCallable(functions, "unlinkParentFromFamilyAdmin");
      await fn({ parentDocId: row.id });
      await load();
      alert(`Unlinked ${row.email} from ${familyLabel(row.familyId)}.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to unlink family.");
    } finally {
      setBusyRowId(null);
    }
  }

  function familyLabel(fid?: string) {
    if (!fid) return "—";
    const fam = families.find((f) => f.id === fid);
    return fam ? `${fam.familyName}` : fid;
  }

  return (
    <div className="card">
      <h2>Roster — Parents</h2>
      <p className="muted">
        Create and manage parent/guardian records. For split households, a parent can have no primary family and
        be connected only to specific students from Roster Links.
      </p>

      {err ? <div className="error">{err}</div> : null}

      <div className="grid2">
        <div>
          <label className="label">Search</label>
          <input
            className="input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Jane, P1002, jane@example.com, family…"
          />
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span>Show inactive</span>
          </label>
        </div>
      </div>

      <hr className="hr" />

      <h3>Add Parent</h3>
      <div className="grid2">
        <div>
          <label className="label">Parent ID</label>
          <input className="input" value="Assigned automatically on save" readOnly />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">First Name</label>
          <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div>
          <label className="label">Last Name</label>
          <input className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div>
          <label className="label">Primary Family (optional)</label>
          <select className="input" value={familyId} onChange={(e) => setFamilyId(e.target.value)}>
            <option value="">No family - student-specific links only</option>
            {families.map((f) => (
              <option key={f.id} value={f.id}>
                {f.familyName} ({f.id})
              </option>
            ))}
          </select>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Family assignment grants access to that household's students. Leave blank for an outside parent or
            guardian, then add exact student links.
          </div>
        </div>
      </div>

      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
        <input
          type="checkbox"
          checked={sendInviteNow}
          onChange={(e) => setSendInviteNow(e.target.checked)}
        />
        <span>Send account setup email now</span>
      </label>

      <button className="btn" disabled={saving} onClick={addParent} style={{ marginTop: 10 }}>
        {saving ? "Saving…" : "Add Parent"}
      </button>
      <Link className="btn secondary" to="/roster/links" style={{ marginTop: 10, marginLeft: 8 }}>
        Add Student-Specific Link
      </Link>

      <hr className="hr" />

      <h3>Parents</h3>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="muted">No parents found.</div>
      ) : (
        <div className="table">
          <div
            className="thead"
            style={{ gridTemplateColumns: "120px 1fr 220px 220px 90px 380px" }}
          >
            <div>Key</div>
            <div>Name</div>
            <div>Email / Link</div>
            <div>Family</div>
            <div>Status</div>
            <div></div>
          </div>

          {filtered.map((r) => {
            const rowBusy = busyRowId === r.id;

            return (
              <div
                className="trow"
                key={r.id}
                style={{ gridTemplateColumns: "120px 1fr 220px 220px 90px 380px" }}
              >
                <div>{r.parentKey}</div>
                <div>{fullName(r)}</div>
                <div>
                  <div>{r.email}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {r.uid ? `Linked • ${r.uid}` : "Not linked"}
                  </div>
                </div>
                <div>
                  <select
                    className="input"
                    value={r.familyId ?? ""}
                    disabled={rowBusy}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) return;
                      void setFamilyForRow(r, value);
                    }}
                  >
                    <option value="">{familyLabel(r.familyId)}</option>
                    {families.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.familyName} ({f.id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className={r.isActive ? "pill ok" : "pill off"}>
                    {r.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <div
                  style={{
                    textAlign: "right",
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                  }}
                >
                  <button className="btn small secondary" disabled={rowBusy} onClick={() => resendInvite(r)}>
                    {rowBusy ? "Working…" : "Invite"}
                  </button>
                  <button className="btn small secondary" disabled={rowBusy} onClick={() => manualLink(r)}>
                    Link
                  </button>
                  <button className="btn small secondary" disabled={rowBusy || !r.familyId} onClick={() => unlinkFamilyForRow(r)}>
                    Unlink Family
                  </button>
                  <button className="btn small secondary" disabled={rowBusy} onClick={() => toggleActive(r)}>
                    {r.isActive ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
